import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../../lib/api';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';

interface Venue {
  id: string;
  name: string;
  addressLine1: string;
  city: string;
  postcode: string;
  commissionPercent: number;
  contactName: string | null;
  contactEmail: string | null;
  isActive: boolean;
}

interface VenueListResponse {
  items: Venue[];
  total: number;
  page: number;
  pageSize: number;
}

export function VenuesList() {
  const navigate = useNavigate();
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [total, setTotal] = useState(0);

  const fetchVenues = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('q', search);

      const response = await apiFetch(`/api/v1/venues?${params}`);

      if (!response.ok) throw new Error('Failed to fetch venues');

      const data: VenueListResponse = await response.json();
      setVenues(data.items);
      setTotal(data.total);
    } catch (error) {
      console.error('Error fetching venues:', error);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    fetchVenues();
  }, [fetchVenues]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchVenues();
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="space-y-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold">Venues</h1>
          <Button onClick={() => navigate('/locations/new?type=VENUE')}>
            Add Venue
          </Button>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>All Venues ({total})</CardTitle>
              <form onSubmit={handleSearch} className="flex gap-2">
                <Input
                  placeholder="Search venues..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-64"
                />
                <Button type="submit" variant="outline">Search</Button>
              </form>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading...</div>
            ) : venues.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">No venues found</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead className="hidden md:table-cell">Address</TableHead>
                    <TableHead className="hidden lg:table-cell">Contact</TableHead>
                    <TableHead className="hidden text-right sm:table-cell">Commission</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {venues.map((venue) => (
                    <TableRow key={venue.id}>
                      <TableCell className="font-medium">{venue.name}</TableCell>
                      <TableCell className="hidden md:table-cell">
                        <div className="text-sm">
                          {venue.addressLine1}
                          <br />
                          <span className="text-muted-foreground">
                            {venue.city}, {venue.postcode}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {venue.contactName || '-'}
                        {venue.contactEmail && (
                          <div className="text-xs text-muted-foreground">{venue.contactEmail}</div>
                        )}
                      </TableCell>
                      <TableCell className="hidden text-right sm:table-cell">{venue.commissionPercent}%</TableCell>
                      <TableCell>
                        <Badge variant={venue.isActive ? 'default' : 'secondary'}>
                          {venue.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/locations/venues/${venue.id}`)}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default VenuesList;
